import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Plus, 
  Send, 
  Twitter, 
  Music2, 
  FileText,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Zap,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentLibrary = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);

  useEffect(() => {
    fetchPosts();
    fetchAccounts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setConnectedAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const handleDelete = async () => {
    if (!postToDelete) return;

    try {
      await axios.delete(`${API}/posts/${postToDelete}`);
      setPosts(prev => prev.filter(p => p.id !== postToDelete));
      toast.success('Post deleted successfully');
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const handlePublish = async (postId) => {
    try {
      await axios.post(`${API}/posts/${postId}/publish`);
      await fetchPosts();
      toast.success('Post published successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to publish post';
      toast.error(message);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'telegram': return <Send className="w-3 h-3" />;
      case 'twitter': return <Twitter className="w-3 h-3" />;
      case 'tiktok': return <Music2 className="w-3 h-3" />;
      default: return <FileText className="w-3 h-3" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform.toLowerCase()) {
      case 'telegram': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'twitter': return 'bg-sky-500/10 text-sky-400 border-sky-500/20';
      case 'tiktok': return 'bg-pink-500/10 text-pink-400 border-pink-500/20';
      default: return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published': return <CheckCircle2 className="w-4 h-4 text-accent" />;
      case 'scheduled': return <Calendar className="w-4 h-4 text-yellow-400" />;
      case 'draft': return <FileText className="w-4 h-4 text-muted-foreground" />;
      case 'failed': return <XCircle className="w-4 h-4 text-destructive" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-accent/10 text-accent border-accent/20">Published</Badge>;
      case 'scheduled':
        return <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Scheduled</Badge>;
      case 'draft':
        return <Badge className="bg-muted text-muted-foreground border-border">Draft</Badge>;
      case 'failed':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPosts = posts.filter(post => {
    if (activeTab === 'all') return true;
    return post.status === activeTab;
  });

  const tabCounts = {
    all: posts.length,
    draft: posts.filter(p => p.status === 'draft').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    published: posts.filter(p => p.status === 'published').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Library</h1>
          <p className="text-muted-foreground">Manage all your posts in one place.</p>
        </div>
        <Link to="/create">
          <Button className="glow-primary" data-testid="create-new-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border/50">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({tabCounts.all})
          </TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-draft">
            Drafts ({tabCounts.draft})
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            Scheduled ({tabCounts.scheduled})
          </TabsTrigger>
          <TabsTrigger value="published" data-testid="tab-published">
            Published ({tabCounts.published})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredPosts.length > 0 ? (
            <div className="space-y-3">
              {filteredPosts.map((post) => (
                <Card 
                  key={post.id} 
                  className="bg-card border-border/50 hover:border-primary/30 transition-colors"
                  data-testid={`library-post-${post.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Status Icon */}
                      <div className="mt-1">
                        {getStatusIcon(post.status)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm line-clamp-2 mb-3">{post.content}</p>
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Platforms */}
                          {post.platforms.map((platform) => (
                            <span 
                              key={platform}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${getPlatformColor(platform)}`}
                            >
                              {getPlatformIcon(platform)}
                              {platform}
                            </span>
                          ))}
                          
                          {/* Dates */}
                          <span className="text-xs text-muted-foreground font-mono flex items-center gap-1 ml-auto">
                            <Clock className="w-3 h-3" />
                            {format(new Date(post.created_at), 'MMM d, HH:mm')}
                          </span>
                          
                          {post.scheduled_at && (
                            <span className="text-xs text-yellow-400 font-mono flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(post.scheduled_at), 'MMM d, HH:mm')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {getStatusBadge(post.status)}
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {(post.status === 'draft' || post.status === 'scheduled') && (
                              <DropdownMenuItem 
                                onClick={() => handlePublish(post.id)}
                                data-testid={`publish-post-${post.id}`}
                              >
                                <Zap className="w-4 h-4 mr-2" />
                                Publish Now
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => {
                                setPostToDelete(post.id);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive focus:text-destructive"
                              data-testid={`delete-post-${post.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border/50">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {activeTab === 'all' ? 'No posts yet' : `No ${activeTab} posts`}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {activeTab === 'all' 
                    ? 'Create your first post to get started' 
                    : `You don't have any ${activeTab} posts`}
                </p>
                <Link to="/create">
                  <Button className="glow-primary" data-testid="empty-state-create-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Post
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContentLibrary;
